import { useRequest, useSize } from 'ahooks'
import { Divider, Popover } from 'antd'
import { mapValues, uniq } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserAvatar } from '@/components'
import { MosaicComponent, getPointPermission } from '@/components'
import { PAGE_RESOURCE_POINTS_ENUM } from '@/schemas'

import { Customer, CustomerFlowStatus } from '../../customer.schema'

export const FlowStatusChart: React.FC<{ customer: Customer }> = ({ customer }) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const isENAndSmallScreen = language === 'en'
  const { canRead } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_成员, customer.permissions)

  const flowStatus = customer.flowStatus
  const flows = (customer.flows || []).filter((f) => {
    return customer.flowStatus !== CustomerFlowStatus.研发暂停中 && f.status === CustomerFlowStatus.研发暂停中
      ? false
      : true
  })
  const ProjectCancelProcessIndex = 0 as const // 项目取消流程
  const ProjectMainProcessIndex = 1 as const // 项目主流程
  const ProjectRejectProcessIndex = 2 as const // 项目拒绝流程
  const ProjectFailProcessIndex = 3 as const // 项目拒绝流程
  const HorizontalStageCount = 9 as const // 水平划分
  const VerticalStageCount = 4 as const // 垂直划分

  const size = useSize(ref)

  const { data } = useRequest<{
    [x: string]: { roles: string[]; users: string[][] }
  }>(
    () => {
      return {
        params: { selfId: customer.selfId },
        url: '/api/customer/flow/action/roles',
        method: 'get',
      }
    },
    { manual: !canRead }
  )

  const [flowStatusStageParams, setFlowStatusStageParams] = React.useState<{
    [x: string]: {
      verticalIndex: number
      horizontalIndex: number
      title: string
      roleUsers?: {
        role: string
        users: string[]
      }[]
    }
  }>({
    [CustomerFlowStatus.待研发审批]: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 0,
      title: t('customer.flowStatus.DevWaitForAudit.stage'),
    },
    [CustomerFlowStatus.研发未开始]: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 1,
      title: t('customer.flowStatus.DevWaitForAudit.button'),
    },
    [CustomerFlowStatus.研发进行中]: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 2,
      title: t('customer.flowStatus.DevNotStarted.button'),
    },
    [CustomerFlowStatus.研发暂停中]: {
      verticalIndex: ProjectCancelProcessIndex,
      horizontalIndex: 3,
      title: t('customer.flowStatus.DevOnGoing.button.pending'),
    },
    [CustomerFlowStatus.待客户验收]: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 4,
      title: t('customer.flowStatus.DevOnGoing.button.delivery'),
    },
    [CustomerFlowStatus.结果追踪中]: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 5,
      title: t('customer.flowStatus.CustomerWaitForValidate.button'),
    },
    [CustomerFlowStatus.结果复核中]: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 6,
      title: t('customer.flowStatus.ResultTracking.action'),
    },
    [CustomerFlowStatus.待项目结项]: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 7,
      title: t('customer.flowStatus.ReleaseWaitForConfirm.button.confirm'),
    },
    [CustomerFlowStatus.项目已结项]: {
      verticalIndex: ProjectMainProcessIndex,
      horizontalIndex: 8,
      title: t('customer.flowStatus.ProjectWaitForFinish.button'),
    },
    [CustomerFlowStatus.项目已取消]: {
      verticalIndex: ProjectCancelProcessIndex,
      horizontalIndex: 8,
      title: t('customer.flowStatus.DevPending.button.cancel'),
    },
    [CustomerFlowStatus.项目已驳回]: {
      verticalIndex: ProjectRejectProcessIndex,
      horizontalIndex: 8,
      title: t('customer.flowStatus.ProjectRejected.stage'),
    },
    [CustomerFlowStatus.项目已失败]: {
      verticalIndex: ProjectFailProcessIndex,
      horizontalIndex: 8,
      title: t('customer.flowStatus.ProjectFailed.button'),
    },
  })

  React.useEffect(() => {
    if (data) {
      const newFlowStatusStageParams = mapValues(flowStatusStageParams, (v, key) => {
        const roleUsers = (data[key]?.roles || []).map((r, index) => {
          const users = data[key].users[index]

          let title = t(`customer.${r}`)

          if (r === 'RDVP') {
            title = t('customer.rdvp')
          }

          if (r === 'FinancialOperator') {
            title = t('production.financialOperation')
          }

          const validFlows = customer.flows.filter((f) => key === f.status)

          return {
            role: title,
            users: uniq(validFlows.length > 0 ? validFlows.filter((v) => v.email).map((v) => v.email!) : users),
          }
        })

        return {
          ...v,
          roleUsers,
        }
      })
      setFlowStatusStageParams(newFlowStatusStageParams)
    }
  }, [data])

  const CircleR = 10 as const
  const subComponents = React.useMemo(() => {
    const shapes = Object.keys(flowStatusStageParams).map((key) => {
      const shape = flowStatusStageParams[key]

      if (!size.width || !size.height) {
        return null
      }

      // 空出最后一列文案空间
      const svgRightAreaSpace = 80
      const svgHeight = size.height
      const svgWidth = size.width - svgRightAreaSpace
      const strokeWidth = 2
      // 第一个状态前线长度占 水平 block 占比
      const prevPaddingRadio = 0.6
      // 水平 block 大小
      const horizontalBlock = Math.floor(svgWidth / HorizontalStageCount)
      // 垂直 block 大小
      const verticalBlock = Math.floor(svgHeight / VerticalStageCount)
      // 状态圆心坐标
      const cx = Math.floor(horizontalBlock * (shape.horizontalIndex + prevPaddingRadio))
      const cy = Math.floor(verticalBlock * (shape.verticalIndex + 0.5))

      const isFlowEnabled = flows.find((f) => {
        const isFind = f.status === key

        // 当用户在研发进行中、研发暂停中时，需判断是否为回退状态
        // 回退状态下不点亮研发交付状态
        if (
          [CustomerFlowStatus.研发进行中, CustomerFlowStatus.研发暂停中].includes(flowStatus) &&
          key === CustomerFlowStatus.待客户验收 &&
          flows.some((f) => f.status === CustomerFlowStatus.待客户验收)
        ) {
          return false
        }

        // 当用户在结果追踪中时，需判断是否为回退状态
        // 回退状态下不点亮结果复核中状态
        if (
          flowStatus === CustomerFlowStatus.结果追踪中 &&
          key === CustomerFlowStatus.结果复核中 &&
          flows.some((f) => f.status === CustomerFlowStatus.结果复核中)
        ) {
          return false
        }
        return isFind
      })
      const circleParam = {
        cx,
        cy,
        r: CircleR,
        fill: isFlowEnabled
          ? [CustomerFlowStatus.项目已驳回, CustomerFlowStatus.项目已取消, CustomerFlowStatus.项目已失败].includes(
              key as any
            )
            ? 'red'
            : flowStatus === CustomerFlowStatus.研发暂停中 && key === CustomerFlowStatus.研发暂停中
              ? 'yellow'
              : 'green'
          : 'transparent',
        stroke: 'black',
      }

      // 状态前的直线
      let prevLineParam = null
      switch (key) {
        case CustomerFlowStatus.待研发审批: {
          prevLineParam = [
            {
              x1: Math.floor(horizontalBlock * shape.horizontalIndex),
              x2: Math.floor(horizontalBlock * (shape.horizontalIndex + prevPaddingRadio) - CircleR),
              y1: cy,
              y2: cy,
              stroke: isFlowEnabled ? 'green' : 'black',
            },
          ]
          break
        }
        case CustomerFlowStatus.研发暂停中: {
          const isEnabled = flowStatus === CustomerFlowStatus.研发暂停中 && key === flowStatus
          prevLineParam = [
            {
              x1: Math.floor(cx - horizontalBlock / 2) - strokeWidth / 2,
              x2: cx - CircleR,
              y1: cy,
              y2: cy,
              stroke: isEnabled ? 'green' : 'black',
            },
            {
              x1: Math.floor(cx - horizontalBlock / 2),
              x2: Math.floor(cx - horizontalBlock / 2),
              y1: cy,
              y2: cy + verticalBlock,
              stroke: isEnabled ? 'green' : 'black',
            },
            {
              x1: Math.floor(cx - horizontalBlock / 2),
              x2: Math.floor(cx - horizontalBlock) + CircleR,
              y1: cy + verticalBlock,
              y2: cy + verticalBlock,
              stroke: isEnabled ? 'green' : 'black',
            },
          ]
          break
        }
        case CustomerFlowStatus.待客户验收: {
          prevLineParam = [
            {
              x1:
                flowStatus === CustomerFlowStatus.研发暂停中
                  ? Math.floor(horizontalBlock * (shape.horizontalIndex - 2 + prevPaddingRadio + 0.5)) -
                    1 +
                    strokeWidth / 2
                  : Math.floor(horizontalBlock * (shape.horizontalIndex - 2 + prevPaddingRadio)) +
                    CircleR +
                    strokeWidth / 2,
              x2: Math.floor(horizontalBlock * (shape.horizontalIndex + prevPaddingRadio)) - CircleR,
              y1: cy,
              y2: cy,
              stroke: isFlowEnabled ? 'green' : 'black',
            },
          ]
          break
        }
        case CustomerFlowStatus.项目已取消:
        case CustomerFlowStatus.项目已驳回:
        case CustomerFlowStatus.项目已失败: {
          break
        }
        default: {
          prevLineParam = [
            {
              x1:
                Math.floor(horizontalBlock * (shape.horizontalIndex - 1 + prevPaddingRadio)) +
                CircleR +
                +strokeWidth / 2,
              x2: Math.floor(horizontalBlock * (shape.horizontalIndex + prevPaddingRadio)) - CircleR,
              y1: cy,
              y2: cy,
              stroke: isFlowEnabled ? 'green' : 'black',
            },
          ]
        }
      }

      const roleUsers = shape.roleUsers || []
      return (
        <g key={key}>
          {prevLineParam
            ? prevLineParam.map((line, index) => <line key={index} strokeWidth={strokeWidth} {...line}></line>)
            : null}
          <Popover
            content={
              <div>
                {canRead ? (
                  roleUsers.length === 0 ? (
                    <span className='text-[#8A8A9A]'>{t('production.flowChart.role.none')}</span>
                  ) : (
                    roleUsers.map((r, index) => {
                      return (
                        <React.Fragment key={r.role}>
                          {index !== 0 ? <Divider /> : null}
                          <div>
                            <span className='font-medium'>{r.role}</span>
                            {(r.users || []).length === 0 ? (
                              <span className='block text-[#8A8A9A]'>{t('production.flowChart.user.none')}</span>
                            ) : (
                              r.users.map((u, index) => {
                                return (
                                  <div className='my-1' key={index}>
                                    <UserAvatar size={20} email={u} showName />
                                  </div>
                                )
                              })
                            )}
                          </div>
                        </React.Fragment>
                      )
                    })
                  )
                ) : (
                  <MosaicComponent />
                )}
              </div>
            }
          >
            <circle className='cursor-pointer' {...circleParam} strokeWidth={strokeWidth}></circle>
          </Popover>
          <text
            fontSize={12}
            x={
              [
                CustomerFlowStatus.项目已取消,
                CustomerFlowStatus.项目已结项,
                CustomerFlowStatus.项目已驳回,
                CustomerFlowStatus.项目已失败,
              ].includes(key as any)
                ? cx + 1.5 * CircleR
                : cx - 2.5 * CircleR
            }
            y={
              [
                CustomerFlowStatus.项目已取消,
                CustomerFlowStatus.项目已结项,
                CustomerFlowStatus.项目已驳回,
                CustomerFlowStatus.项目已失败,
              ].includes(key as any)
                ? cy + CircleR / 2
                : isENAndSmallScreen &&
                    [
                      CustomerFlowStatus.研发未开始,
                      CustomerFlowStatus.待客户验收,
                      CustomerFlowStatus.结果复核中,
                    ].includes(key as any)
                  ? cy - 3 * CircleR
                  : cy + 3 * CircleR
            }
          >
            {shape.title}
          </text>
        </g>
      )
    })
    return shapes
  }, [size, customer.flowStatus, flowStatusStageParams])

  if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, customer.permissions).canRead) {
    return <MosaicComponent />
  }

  return (
    <div ref={ref}>
      <svg className='w-full' height={160}>
        {subComponents}
      </svg>
    </div>
  )
}
